




import { Component, Vue } from 'vue-property-decorator';
import { ModalOptionsV2 } from '@/components/Modal/ModalOptions';
import eventBus from '../../utils/eventBus';
import { Campaign } from '@/models/marketingCenter/sellCoins';
import { treatError } from '@/utils';
import SellCoinsService from '@/api/SellCoinsService';
import DateUtils from '@/utils/DateUtils';
import { EProgramStatusStatus } from '@/models/program-status';
import flagKeys from '@/constants/flagKeys';
import BusinessService from '@/api/BusinessService';
import { DashboardScreen } from '@/api/enums/dashboard-screen.enum';

@Component({})
export default class SellCoinsModalProps extends Vue {
  sellCoinsService = new SellCoinsService();
  businessService = new BusinessService();

  campaign: any = {};
  automaticCampaign: any = {};
  campaignStatusType: string = '';
  iconName: string = 'warning.svg';

  campaignStatusMap = new Map<string, string>([
    ['campaignDeactivation', 'deactivated'],
    ['campaignAlreadyActive', 'activated'],
    ['campaignActivation', 'activated'],
    ['campaignAutomaticActivation', 'deactivated']
  ]);

  // check to see if the selected campaign is closed
  get isSelectedCampaignClosed() {
    return this.campaign.status == 'closed';
  }

  get businessInfo() {
    return this.$store.getters['business/getBusinessInfo'].business;
  }

  // check to see if there is a campaign data
  hasActiveCampaign(campaign: Campaign) {
    return Object.keys(campaign).length !== 0;
  }

  formatDate(date: any) {
    if (date && date !== null) {
      const [formatedDate] = date.split('T');
      return DateUtils.formattedDate({ date: formatedDate });
    }

    return 'No expiration date';
  }

  newCampaignEndDateFormatted(newCampaignData: Campaign) {
    if (newCampaignData && newCampaignData.endDate) {
      return DateUtils.formattedDate({
        date: newCampaignData.endDate
      });
    }

    return 'No expiration date';
  }

  async getActiveCampaign() {
    try {
      // this.loading = true;
      this.$showLoaderWithLocker();

      const response = await this.sellCoinsService.getActiveCampaign();

      return response.data;
    } catch (e) {
      treatError(e);
    } finally {
      // this.loading = false;
      this.$hideLoader();
    }
  }

  async deactivateCampaign() {
    const activeCampaign = (await this.getActiveCampaign()) as Campaign;

    /*
     * In case there is no active campaign but exists an
     * automatic created campaign it should show
     * the campaign override activation modal
     */
    // if (this.automaticCampaign) {
    //   this.campaignStatusType = 'campaignAutomaticActivation';
    //   activeCampaign.endDate = this.formatDate(activeCampaign.endDate);
    //   this.openCampaignStatusManagementModal(
    //     activeCampaign,
    //     this.automaticCampaign
    //   );
    //   return;
    // }

    this.campaignStatusType = 'campaignDeactivation';
    this.openCampaignStatusManagementModal();
  }

  async handleActivation() {
    const activeCampaign = (await this.getActiveCampaign()) as Campaign;
    /*
     * In case the selected campaign to activate is closed
     * it should show the campaign closed modal
     */
    if (this.isSelectedCampaignClosed) {
      this.campaignStatusType = 'campaignClosed';
      this.openCampaignStatusManagementModal();
      return;
    }

    /*
     * In case the loyalt ratio is higher than the
     * campaign ratio it should show the warning modal
     */
    const businessInfo = this.$store.getters['business/getBusinessInfo']
      .business;
    const rewardStatus = this.$store.getters['business/getRewardStatus']
      ? this.$store.getters['business/getRewardStatus'].rewardStatus
      : null;
    if (
      businessInfo.coinRewardPercentage > this.campaign.incentiveValue &&
      (!rewardStatus ||
        (rewardStatus === EProgramStatusStatus.ACTIVE ||
          rewardStatus === EProgramStatusStatus.PARTIALLY_ACTIVE ||
          rewardStatus === EProgramStatusStatus.PENDING)) &&
      this.$store.getters['flags/getFlags'].includes(
        flagKeys.dashboardSellCoinsAndLoyaltVerify
      )
    ) {
      this.campaignStatusType = 'campaignLoyaltRatioIssue';
      this.openCampaignStatusManagementModal(activeCampaign, this.campaign);
      return;
    }

    /*
     * In case there is no active campaign it should show the
     * campaign activation modal
     */
    if (!this.hasActiveCampaign(activeCampaign)) {
      this.campaignStatusType = 'campaignActivation';
      this.openCampaignStatusManagementModal();
      return;
    }

    /*
     * In case there is active campaign it should show
     * the campaign override activation modal
     */
    this.campaignStatusType = 'campaignAlreadyActive';
    activeCampaign.endDate = this.formatDate(activeCampaign.endDate);
    this.openCampaignStatusManagementModal(activeCampaign, this.campaign);
  }

  getInTouch() {
    this.$analytics({
      eventCategory: 'Sell Coins Campaign | Ask for Support',
      eventAction: 'click',
      eventLabel: 'Clicked on the "Ask for Support!" button'
    });

    location.href = 'mailto:support@twism.com';
  }

  gotoCreateCampaign() {
    this.$router.push('/sell-coins/campaign/create');
  }

  openCampaignStatusManagementModal(
    activeCampaignData?: any,
    newCampaignData?: any
  ) {
    eventBus.$emit(
      'openSubModal',
      new ModalOptionsV2({
        icon: 'none',
        hideCancelButton: true,
        confirmEvent: '',
        componentToRender: 'SellCoinsCampaignStatusManagementModal',
        fullWidth: true,
        removeBackground: true
      }),
      {
        iconName: this.iconName,
        campaignType: this.campaignStatusType,
        activeCampaignData,
        newCampaignData: {
          ...newCampaignData,
          endDate: this.newCampaignEndDateFormatted(newCampaignData)
        },
        onConfirm: async () => {
          if (
            this.campaignStatusType === 'campaignClosed' ||
            this.campaignStatusType === 'campaignLoyaltRatioIssue'
          ) {
            this.gotoCreateCampaign();
            return;
          }

          if (this.campaignStatusType === 'campaignNotFound') {
            this.getInTouch();
            return;
          }

          if (this.campaignStatusType === 'campaignAlreadyActive') {
            await this.sellCoinsService.deactivateActiveCampaign();
          }

          if (this.campaignStatusType === 'campaignDeactivation') {
            await this.businessService.updateSellCoins(
              false,
              DashboardScreen.Dashboard
            );
            this.$store.dispatch('business/SET_BUSINESS_INFO', {
              ...this.businessInfo,
              sellCoins: false
            });
          } else if (!this.campaign.isCampaignActive) {
            await this.businessService.updateSellCoins(
              true,
              DashboardScreen.Dashboard
            );
            this.$store.dispatch('business/SET_BUSINESS_INFO', {
              ...this.businessInfo,
              sellCoins: true
            });
          }

          this.changeCampaignStatus(
            newCampaignData ? newCampaignData.id : null
          );
        }
      }
    );
  }

  handleClose() {
    eventBus.$emit('closeModal');
  }

  async changeCampaignStatus(campaignId?: string) {
    // this.loading = true;

    try {
      this.sellCoinsService.disableInterception();

      const response = await this.sellCoinsService.changeCampaignStatus(
        this.campaign.id
      );

      // this.loading = false;

      if (response.status === 200) {
        if (this.campaignStatusType === 'campaignAlreadyActive') {
          eventBus.$emit('disableSellingCoinsCampaigns');
        }

        if (
          this.campaignStatusType === 'campaignAutomaticActivation' &&
          campaignId
        ) {
          await this.sellCoinsService.changeCampaignStatus(campaignId);
          eventBus.$emit('enableAutomaticSellingCoinCampaign');
        }

        this.handleClose();
        // this.isCampaignActive = !this.isCampaignActive;
        window.parent.postMessage(
          {
            type: 'myprograms:reload',
            data: {
              overviewData: [
                'campaignDeactivation',
                'campaignActivation'
              ].includes(this.campaignStatusType)
                ? {
                    hasActiveSellcoins: !this.campaign.isCampaignActive
                  }
                : {}
            }
          },
          '*'
        );
        this.$notification(
          `Campaign successfully ${this.campaignStatusMap.get(
            this.campaignStatusType
          )}`
        );
      }
    } catch (err) {
      if (err instanceof Error) {
        this.handleErrors(err);
      }
    }
  }

  handleErrors(e: any): void {
    // Return 404 (campaign not found)

    if (e.response.status === 404) {
      this.iconName = 'error.svg';
      this.campaignStatusType = 'campaignNotFound';
      this.openCampaignStatusManagementModal();
      return;
    }

    this.$notification(treatError(e));
  }
}
